import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, ShieldCheck, Target } from "lucide-react";
import { api } from "@/api";
import ProductCard from "@/components/ProductCard";
import WhatsappStrip from "@/components/WhatsappStrip";

const DRIVERS_IMG = "https://images.pexels.com/photos/33855229/pexels-photo-33855229.jpeg";

const EQUIP_CATS = [
  { slug: "drivers", label: "Drivers", img: DRIVERS_IMG, span: "md:col-span-2 md:row-span-2" },
  { slug: "irons", label: "Irons", img: "https://images.pexels.com/photos/54123/pexels-photo-54123.jpeg", span: "" },
  { slug: "putters", label: "Putters", img: "https://images.pexels.com/photos/6256828/pexels-photo-6256828.jpeg", span: "" },
  { slug: "wedges", label: "Wedges", img: "https://images.pexels.com/photos/8007403/pexels-photo-8007403.jpeg", span: "" },
  { slug: "hybrids", label: "Hybrids", img: "https://images.pexels.com/photos/17663497/pexels-photo-17663497.jpeg", span: "" },
];

const FEATURES = [
  { icon: Wrench, title: "Custom Fitting", desc: "Every club dialled to your swing at our fitting studios." },
  { icon: ShieldCheck, title: "100% Genuine", desc: "J&B Golf — sole PING distributor in India. Full warranty." },
  { icon: Target, title: "Expert Support", desc: "PING-trained specialists to guide every purchase." },
];

const renderHeading = (text) =>
  (text || "").split("\n").map((line, i) => (
    <span key={i}>{line}<br /></span>
  ));

function HeroCarousel({ banners }) {
  const [index, setIndex] = useState(0);
  const count = banners.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, count]);

  if (count === 0) return null;

  return (
    <section className="relative h-[85vh] min-h-[560px] w-full overflow-hidden" data-testid="hero-carousel">
      {banners.map((b, i) => (
        <div
          key={b.id}
          className="absolute inset-0 transition-opacity duration-700 ease-out"
          style={{ opacity: i === index ? 1 : 0, zIndex: i === index ? 1 : 0 }}
          aria-hidden={i !== index}
        >
          <img src={b.image} alt={b.heading || "PING"} className="absolute inset-0 w-full h-full" style={{ objectFit: b.image_fit || "cover", objectPosition: b.image_position || "center" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          <div className="container-ping relative h-full flex flex-col justify-end pb-20">
            <div className={`max-w-2xl ${i === index ? "fade-up" : ""}`}>
              {b.kicker && <span className="label-caps text-[#E31837] bg-white px-3 py-1 inline-block mb-5">{b.kicker}</span>}
              <h1 className="heading-1 text-white">{renderHeading(b.heading)}</h1>
              {b.subtext && <p className="mt-6 text-lg text-white/80 max-w-lg leading-relaxed">{b.subtext}</p>}
              {b.button_label && (
                <div className="mt-8">
                  <Link to={b.button_link || "/shop"} className="bg-[#E31837] text-white px-8 py-4 label-caps btn-transition hover:bg-[#b3122c] active:scale-95 inline-flex items-center gap-2" data-testid={`hero-cta-${i}`}>
                    {b.button_label} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {count > 1 && (
        <div className="absolute bottom-8 right-8 z-10 flex gap-2" data-testid="hero-dots">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1.5 btn-transition ${i === index ? "w-8 bg-[#E31837]" : "w-4 bg-white/60 hover:bg-white"}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [shopFeatured, setShopFeatured] = useState([]);

  useEffect(() => {
    api.get("/banners").then(({ data }) => setBanners(data));
    api.get("/site-settings").then(({ data }) => setSettings(data));
    api.get("/products", { params: { group: "shop" } }).then(({ data }) => {
      const feat = data.filter((p) => p.featured);
      setShopFeatured((feat.length >= 4 ? feat : data).slice(0, 4));
    });
  }, []);

  const promo = settings?.promo;
  const fitting = settings?.fitting;
  const techBanner = settings?.tech_banner;

  return (
    <div data-testid="home-page">
      <HeroCarousel banners={banners} />

      {/* FEATURES STRIP */}
      <section className="border-b border-border">
        <div className="container-ping grid grid-cols-1 sm:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={i} className={`flex items-start gap-4 py-8 px-2 lg:px-6 ${i < 2 ? "sm:border-r border-border" : ""}`}>
              <f.icon className="h-7 w-7 text-[#E31837] shrink-0" strokeWidth={1.5} />
              <div>
                <h4 className="font-head font-bold uppercase tracking-tight text-lg leading-none">{f.title}</h4>
                <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EQUIPMENT CATEGORY BENTO */}
      <section className="container-ping py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="label-caps text-[#E31837]">The Equipment</span>
            <h2 className="heading-2 mt-2">Built To Be Fit</h2>
          </div>
          <Link to="/equipment" className="hidden sm:flex items-center gap-2 label-caps hover:text-[#E31837] btn-transition">
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[220px] gap-4">
          {EQUIP_CATS.map((c) => (
            <Link key={c.slug} to={`/equipment/${c.slug}`} className={`relative group overflow-hidden ${c.span}`} data-testid={`category-tile-${c.slug}`}>
              <img src={settings?.category_images?.[c.slug] || c.img} alt={c.label} className="absolute inset-0 w-full h-full card-hover-img group-hover:scale-105" style={{ objectFit: settings?.category_image_fits?.[c.slug] || "cover", objectPosition: settings?.category_image_positions?.[c.slug] || "center" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 flex items-center justify-between w-full">
                <h3 className="font-head text-2xl md:text-3xl font-black uppercase tracking-tight text-white">{c.label}</h3>
                <ArrowRight className="h-6 w-6 text-white -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 btn-transition" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PROMO BLOCK (editable) */}
      {promo && promo.image && (
        <section className="bg-[#111111] text-white">
          <div className="container-ping grid md:grid-cols-2 gap-0 items-stretch">
            <Link to={promo.button_link || "#"} className="relative aspect-square md:aspect-auto overflow-hidden group" data-testid="promo-banner">
              <img src={promo.image} alt={promo.heading} className="w-full h-full card-hover-img group-hover:scale-105" style={{ objectFit: promo.image_fit || "cover", objectPosition: promo.image_position || "center" }} />
            </Link>
            <div className="flex flex-col justify-center p-10 md:p-16">
              {promo.kicker && <span className="label-caps text-[#E31837]">{promo.kicker}</span>}
              <h2 className="heading-2 mt-3 text-white">{renderHeading(promo.heading)}</h2>
              {promo.text && <p className="mt-6 text-white/70 leading-relaxed max-w-md">{promo.text}</p>}
              {promo.button_label && (
                <Link to={promo.button_link || "#"} className="mt-8 inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 label-caps btn-transition hover:bg-white/90 active:scale-95 w-fit" data-testid="promo-cta">
                  {promo.button_label} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PRO SHOP FEATURED */}
      <section className="bg-[#F4F4F5] py-20">
        <div className="container-ping">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-caps text-[#E31837]">Shop Online</span>
              <h2 className="heading-2 mt-2">The Pro Shop</h2>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center gap-2 label-caps hover:text-[#E31837] btn-transition">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="shop-featured-grid">
            {shopFeatured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* TECH BANNER (editable via Admin → Content) */}
      {techBanner && (techBanner.image || techBanner.heading) && (
        <section className="relative bg-white overflow-hidden border-t border-border" data-testid="tech-banner-section">
          <div className="container-ping grid md:grid-cols-2 gap-12 items-center py-20">
            <div>
              {techBanner.kicker && <span className="label-caps text-[#E31837]" data-testid="tech-banner-kicker">{techBanner.kicker}</span>}
              {techBanner.heading && <h2 className="heading-2 mt-3" data-testid="tech-banner-heading">{renderHeading(techBanner.heading)}</h2>}
              {techBanner.text && <p className="mt-6 text-muted-foreground leading-relaxed max-w-md" data-testid="tech-banner-text">{techBanner.text}</p>}
              {techBanner.button_label && (
                <Link to={techBanner.button_link || "/technology"} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 label-caps btn-transition hover:bg-black/80 active:scale-95" data-testid="tech-banner-btn">
                  {techBanner.button_label} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
            <div className="relative h-[360px]">
              {techBanner.image && <img src={techBanner.image} alt={techBanner.heading || "PING technology"} className="absolute inset-0 w-full h-full" style={{ objectFit: techBanner.image_fit || "cover", objectPosition: techBanner.image_position || "center" }} />}
            </div>
          </div>
        </section>
      )}

      {/* FITTING CTA (editable) */}
      {fitting && fitting.image && (
        <section className="relative h-[460px] overflow-hidden">
          <img src={fitting.image} alt={fitting.heading} className="absolute inset-0 w-full h-full" style={{ objectFit: fitting.image_fit || "cover", objectPosition: fitting.image_position || "center" }} />
          <div className="absolute inset-0 bg-black/55" />
          <div className="container-ping relative h-full flex flex-col items-center justify-center text-center text-white">
            {fitting.kicker && <span className="label-caps text-white/80">{fitting.kicker}</span>}
            <h2 className="heading-1 mt-4 max-w-3xl">{renderHeading(fitting.heading)}</h2>
            {fitting.text && <p className="mt-4 text-white/80 max-w-lg">{fitting.text}</p>}
            {fitting.button_label && (
              <Link to={fitting.button_link || "/dealers"} className="mt-8 bg-[#E31837] text-white px-10 py-4 label-caps btn-transition hover:bg-[#b3122c] active:scale-95" data-testid="book-fitting-btn">
                {fitting.button_label}
              </Link>
            )}
          </div>
        </section>
      )}

      <WhatsappStrip />
    </div>
  );
}
